import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ref, remove, set } from 'firebase/database';
import {
  FaBookOpen,
  FaFileDownload,
  FaLink,
  FaLock,
  FaPaperclip,
  FaVideo,
} from 'react-icons/fa';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useStudentCourses } from '../../hooks/useLmsData';
import { useBranches } from '../../hooks/useBranches';
import { resolveBranchRef } from '../../utils/branchResolver';
import { submitEnrollmentRequest } from '../../utils/lmsMutations';
import {
  normalizeLessonAttachments,
  normalizeLessonVideos,
} from '../../utils/lessonMedia';
import { buildScheduledLessons } from '../../utils/studentSchedule';
import Button from '../ui/Button';
import JoinRequestModal from './JoinRequestModal';
import {
  StudentActionLink,
  StudentCard,
  StudentEmpty,
  StudentPage,
  StudentProgressBar,
  StudentSection,
} from './StudentUI';
import {
  formatStudentCurrency,
  formatStudentDate,
  getStudentCourseAction,
} from '../../utils/studentExperience';
import {
  getCourseSuspension,
  isLessonRestrictedBySuspension,
  SUSPENSION_LEVELS,
  suspensionMessage,
} from '../../utils/accessSuspensions';

const CourseDetails = () => {
  const { id, courseId } = useParams();
  const courseIdentifier = courseId || id;
  const { currentUser } = useAuth();
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const {
    data: courseData,
    loading,
    error,
    refresh,
  } = useStudentCourses(currentUser?.uid);
  const [completedLessons, setCompletedLessons] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [submitError, setSubmitError] = useState('');

  const selectedCourse = courseData?.courses?.[courseIdentifier];
  const enrollment = courseData?.enrollments?.find(
    (item) => item.courseId === courseIdentifier,
  );
  const requestForCourse = courseData?.enrollmentRequests?.find(
    (item) =>
      item.courseId === courseIdentifier &&
      (item.status === 'pending' || item.status === 'approved'),
  );
  const isMyCoursePage = Boolean(courseId);
  const isEnrolled = enrollment?.status === 'active';
  const courseSuspension = getCourseSuspension({
    courseId: courseIdentifier,
    user: courseData?.user || currentUser,
    student: courseData?.student,
    enrollment,
    course: selectedCourse,
  });
  const isCourseSuspended = courseSuspension?.level === SUSPENSION_LEVELS.COURSE;
  const courseAction = getStudentCourseAction({
    id: courseIdentifier,
    status: isEnrolled ? 'active' : 'available',
  });

  const upcomingBatches = useMemo(
    () =>
      (selectedCourse?.batches || [])
        .map((batch, index) => ({
          ...batch,
          sourceIndex: index,
        }))
        .filter((batch) => !isBatchExpired(batch)),
    [selectedCourse],
  );

  const scheduledLessons = useMemo(
    () =>
      buildScheduledLessons({
        course: selectedCourse,
        enrollment,
      }),
    [selectedCourse, enrollment],
  );
  const availableLessons = useMemo(
    () =>
      scheduledLessons.filter(
        (lesson) =>
          lesson.isAvailable &&
          !isLessonRestrictedBySuspension(courseSuspension, lesson),
      ),
    [courseSuspension, scheduledLessons],
  );
  const upcomingLessons = useMemo(
    () => scheduledLessons.filter((lesson) => lesson.isUpcoming),
    [scheduledLessons],
  );
  const shouldPrioritizeLessons =
    isEnrolled && !isCourseSuspended && availableLessons.length > 0;

  useEffect(() => {
    if (!enrollment?.completedLessons) return;
    setCompletedLessons(enrollment.completedLessons);
  }, [enrollment?.completedLessons]);

  const handleJoinRequest = async (batchId, additionalInfo) => {
    try {
      setSubmitError('');
      await submitEnrollmentRequest({
        courseId: courseIdentifier,
        batchId,
        currentUser,
        courseName: selectedCourse.name,
        additionalInfo,
      });
      setIsModalOpen(false);
      refresh();
    } catch (requestError) {
      console.error('Error submitting join request:', requestError);
      setSubmitError('Could not send your request. Please try again.');
    }
  };

  const handleLessonCompletion = async (lessonId, index) => {
    if (!enrollment?.id) return;

    const uniqueLessonId = lessonId || `${courseIdentifier}_lesson_${index}`;
    const alreadyCompleted =
      completedLessons[uniqueLessonId] === true ||
      completedLessons[uniqueLessonId]?.completed;
    const nextValue = alreadyCompleted
      ? false
      : {
          completed: true,
          completedAt: new Date().toISOString(),
        };

    const lessonRef = ref(
      db,
      `enrollments/${enrollment.id}/completedLessons/${uniqueLessonId}`,
    );

    if (nextValue === false) {
      await remove(lessonRef);
    } else {
      await set(lessonRef, nextValue);
    }

    const nextCompletedLessons = {
      ...completedLessons,
      [uniqueLessonId]: nextValue,
    };

    if (nextValue === false) {
      delete nextCompletedLessons[uniqueLessonId];
    }

    const totalLessons = selectedCourse.lessons?.length || 1;
    const completedCount = Object.values(nextCompletedLessons).filter(
      (value) => value === true || value?.completed,
    ).length;
    const progress = Math.round((completedCount / totalLessons) * 100);

    await set(ref(db, `enrollments/${enrollment.id}/progress`), progress);
    setCompletedLessons(nextCompletedLessons);
    refresh();
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-stone-200 border-t-[#d61f74]" />
      </div>
    );
  }

  if (error || !selectedCourse) {
    return (
      <div className="py-8 text-center text-red-600">
        {submitError || error?.message || 'Course not found'}
      </div>
    );
  }

  const imageSrc =
    selectedCourse.imageUrl ||
    selectedCourse.coverImage ||
    '/default-course-image.jpg';

  return (
    <>
      <StudentPage
        title={selectedCourse.name}
        subtitle={selectedCourse.description || 'Course details'}
        action={
          <Link
            to={isMyCoursePage ? '/courses/my-courses' : '/courses/available'}
            className="inline-flex min-h-[48px] items-center justify-center rounded-2xl border border-stone-300 px-4 text-sm font-semibold text-stone-700 transition hover:bg-stone-50"
          >
            Back
          </Link>
        }
      >
        {courseSuspension ? (
          <StudentCard className="border-amber-200 bg-amber-50">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-amber-950">
                  {courseSuspension.level === SUSPENSION_LEVELS.FUTURE_LESSONS
                    ? 'Future lesson access paused'
                    : 'Course access paused'}
                </p>
                <p className="mt-1 text-sm text-amber-900">
                  {suspensionMessage(courseSuspension, selectedCourse.name)}
                </p>
              </div>
              <StudentActionLink to="/payments" variant="secondary">
                Open payments
              </StudentActionLink>
            </div>
          </StudentCard>
        ) : null}

        {!shouldPrioritizeLessons ? (
          <div className="overflow-hidden rounded-[28px] border border-stone-200 bg-white shadow-sm">
            <img
              src={imageSrc}
              alt={selectedCourse.name}
              className="h-56 w-full object-cover md:h-72"
              onError={(event) => {
                event.currentTarget.onerror = null;
                event.currentTarget.src = '/default-course-image.jpg';
              }}
            />
            <div className="space-y-5 p-5 sm:p-6">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold text-stone-900">
                    {selectedCourse.name}
                  </h2>
                  <p className="text-sm leading-6 text-stone-600">
                    {selectedCourse.description || 'Course details'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {isEnrolled ? (
                    <StudentActionLink
                      to={courseAction.to}
                      className={isCourseSuspended ? 'pointer-events-none opacity-60' : ''}
                    >
                      {isCourseSuspended ? 'Access paused' : 'Open course'}
                    </StudentActionLink>
                  ) : null}
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <FactCard label="Fee" value={formatStudentCurrency(selectedCourse.fees?.fullCourseFee)} />
                <FactCard label="Registration" value={formatStudentCurrency(selectedCourse.fees?.registrationFee)} />
                <FactCard
                  label="Duration"
                  value={
                    selectedCourse.duration?.isOneDay
                      ? 'One day'
                      : `${selectedCourse.duration?.months || 0} months`
                  }
                />
                <FactCard label="Lessons" value={selectedCourse.lessons?.length || 0} />
              </div>
            </div>
          </div>
        ) : (
          <StudentCard className="bg-white">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-2xl font-semibold tracking-tight text-stone-950">
                  {selectedCourse.name}
                </p>
                <p className="mt-1 text-sm text-stone-600">
                  {availableLessons.length} lesson{availableLessons.length === 1 ? '' : 's'} ready now
                  {upcomingLessons.length ? ` · ${upcomingLessons.length} upcoming` : ''}
                </p>
              </div>
              <div className="min-w-[11rem]">
                <StudentProgressBar value={Number(enrollment?.progress || 0)} />
              </div>
            </div>
          </StudentCard>
        )}

        {!isEnrolled ? (
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <StudentSection title="Course details">
              <div className="space-y-5">
                <div className="grid gap-3 md:grid-cols-2">
                  <FactCard
                    label="Level"
                    value={formatLevel(selectedCourse.level || selectedCourse.academicLevel)}
                  />
                  <FactCard label="Type" value={selectedCourse.type || 'Course'} />
                  <FactCard
                    label="Installments"
                    value={selectedCourse.fees?.numberOfInstallments || 1}
                  />
                  <FactCard
                    label="Next batch"
                    value={formatBatchDate(upcomingBatches?.[0]?.startDate)}
                  />
                </div>

                <div className="rounded-2xl bg-stone-50 p-5">
                  <h3 className="text-base font-semibold text-stone-900">Description</h3>
                  <p className="mt-2 text-sm leading-7 text-stone-600">
                    {selectedCourse.description || 'Course details will appear here.'}
                  </p>
                </div>

                {selectedCourse.curriculum?.length ? (
                  <div className="rounded-2xl bg-stone-50 p-5">
                    <h3 className="text-base font-semibold text-stone-900">Curriculum</h3>
                    <div className="mt-4 space-y-3">
                      {selectedCourse.curriculum.map((item, index) => (
                        <div
                          key={`${item.title || 'topic'}-${index}`}
                          className="rounded-2xl bg-white px-4 py-4"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#fce7f3] text-sm font-semibold text-[#b91c66]">
                              {index + 1}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-stone-900">
                                {item.title || `Topic ${index + 1}`}
                              </p>
                              {item.description ? (
                                <p className="mt-1 text-sm leading-6 text-stone-600">
                                  {item.description}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            </StudentSection>

            <div className="space-y-5">
              <StudentSection title="What you need to know">
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
                  <FactCard label="Batches" value={upcomingBatches.length || 0} />
                  <FactCard
                    label="Class days"
                    value={
                      upcomingBatches?.[0]?.schedule?.days?.join(', ') || 'Not set'
                    }
                  />
                  <FactCard
                    label="Class time"
                    value={upcomingBatches?.[0]?.schedule?.timeSlot || 'Not set'}
                  />
                  <FactCard
                    label="Minimum requirement"
                    value={formatMinimumQualifications(selectedCourse.minimumQualifications)}
                  />
                </div>
              </StudentSection>

              <StudentSection title="How registration works">
                {submitError ? (
                  <div className="mb-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
                    {submitError}
                  </div>
                ) : null}
                <div className="space-y-3 text-sm text-stone-600">
                  <p>1. Open an upcoming batch.</p>
                  <p>2. Review payment method and instructions.</p>
                  <p>3. Send your request now, or pay later from Payments.</p>
                  <p>4. Start the course after approval.</p>
                </div>
              </StudentSection>
            </div>
          </div>
        ) : null}

        {!isEnrolled ? (
          <StudentSection title="Batches">
            {!upcomingBatches.length ? (
              <StudentEmpty
                title="No upcoming batch"
                message="Please check again later for the next batch."
              />
            ) : (
              <div className="grid gap-4 xl:grid-cols-2">
                {upcomingBatches.map((batch, index) => (
                  <StudentCard
                    key={batch.batchId || batch.sourceIndex || index}
                    className="bg-white"
                  >
                    <div className="space-y-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-2">
                          <p className="text-xl font-semibold text-stone-900">
                            {batch.batchId || `Batch ${index + 1}`}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            <BatchMetaBadge>
                              {resolveBranchRef(batch, {
                                byId: branchesById,
                                bySlug: branchesBySlug,
                              })?.name || 'Branch not set'}
                            </BatchMetaBadge>
                            <BatchMetaBadge>
                              {formatBatchDate(batch.startDate)}
                            </BatchMetaBadge>
                          </div>
                        </div>
                        <Button
                          onClick={() => {
                            setSelectedBatchId(batch.batchId || batch.sourceIndex);
                            setIsModalOpen(true);
                          }}
                          disabled={Boolean(requestForCourse)}
                        >
                          {requestForCourse ? 'Request sent' : 'Register'}
                        </Button>
                      </div>
                      <div className="grid gap-3 md:grid-cols-2">
                        <BatchInfoItem
                          label="Class days"
                          value={batch.schedule?.days?.join(', ') || 'Not set'}
                        />
                        <BatchInfoItem
                          label="Time"
                          value={batch.schedule?.timeSlot || 'Not set'}
                        />
                        <BatchInfoItem
                          label="Start date"
                          value={formatBatchDate(batch.startDate)}
                        />
                        <BatchInfoItem
                          label="Branch"
                          value={
                            resolveBranchRef(batch, {
                              byId: branchesById,
                              bySlug: branchesBySlug,
                            })?.name || 'Not set'
                          }
                        />
                      </div>
                    </div>
                  </StudentCard>
                ))}
              </div>
            )}
          </StudentSection>
        ) : (
          <>
            <StudentSection title="Lessons">
              <div className="space-y-4">
                <StudentProgressBar value={Number(enrollment?.progress || 0)} />
                {!availableLessons.length ? (
                  <StudentEmpty
                    title="No lesson open yet"
                    message="Your lessons will appear here when they are ready."
                  />
                ) : (
                  availableLessons.map((lesson, index) => {
                    const completedValue =
                      completedLessons[
                        lesson.id || `${courseIdentifier}_lesson_${lesson.lessonIndex ?? index}`
                      ];
                    const isCompleted =
                      completedValue === true || completedValue?.completed;

                    return (
                      <LessonCard
                        key={lesson.id || index}
                        lesson={lesson}
                        index={lesson.lessonIndex ?? index}
                        locked={false}
                        isCompleted={isCompleted}
                        onToggleComplete={() =>
                          handleLessonCompletion(lesson.id, index)
                        }
                        courseIdentifier={courseIdentifier}
                        isMyCoursePage={isMyCoursePage}
                      />
                    );
                  })
                )}
              </div>
            </StudentSection>

            <StudentSection title="Upcoming lessons">
              {!upcomingLessons.length ? (
                <StudentEmpty
                  title="No upcoming lesson"
                  message="Your next lesson date will appear here."
                />
              ) : (
                <div className="space-y-4">
                  {upcomingLessons.map((lesson, index) => (
                    <LessonCard
                      key={lesson.id || index}
                      lesson={lesson}
                      index={lesson.lessonIndex ?? index}
                      locked
                      courseIdentifier={courseIdentifier}
                      isMyCoursePage={isMyCoursePage}
                    />
                  ))}
                </div>
              )}
            </StudentSection>
          </>
        )}
      </StudentPage>

      <JoinRequestModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        batch={upcomingBatches.find(
          (item, index) =>
            String(item.batchId) === String(selectedBatchId) ||
            String(item.sourceIndex) === String(selectedBatchId),
        )}
        onSubmit={(additionalInfo) =>
          handleJoinRequest(selectedBatchId, additionalInfo)
        }
      />
    </>
  );
};

const LessonCard = ({
  lesson,
  index,
  locked,
  isCompleted,
  onToggleComplete,
  courseIdentifier,
  isMyCoursePage,
}) => {
  const lessonSlug =
    lesson.id != null && String(lesson.id).trim() !== ''
      ? lesson.id
      : String(index);
  const readerTo = isMyCoursePage
    ? `/courses/my-courses/${courseIdentifier}/lessons/${lessonSlug}`
    : `/courses/${courseIdentifier}/lessons/${lessonSlug}`;
  const attachmentCount = normalizeLessonAttachments(lesson).length;

  return (
    <StudentCard className="bg-white">
      <div className="space-y-4">
        {lesson.coverImage ? (
          <img
            src={lesson.coverImage}
            alt={lesson.title}
            className="h-52 w-full rounded-[20px] object-cover"
          />
        ) : null}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-stone-900">
              Lesson {index + 1}: {lesson.title}
            </h3>
            <p className="mt-1 text-sm text-stone-500">
              {locked
                ? `Opens ${formatStudentDate(lesson.scheduledDateIso || lesson.date)}`
                : formatStudentDate(lesson.scheduledDateIso || lesson.date)}
            </p>
          </div>
          {locked ? <FaLock className="mt-1 text-stone-400" /> : null}
        </div>
        <p className="text-sm leading-6 text-stone-600">{lesson.description}</p>
        {lesson.scheduledTime ? (
          <p className="text-sm font-medium text-stone-500">{lesson.scheduledTime}</p>
        ) : null}
        {lesson.assignment?.brief ? (
          <div className="rounded-2xl bg-stone-50 p-4 text-sm text-stone-700">
            <p className="font-semibold text-stone-900">
              {lesson.assignment.title || 'Assignment'}
            </p>
            <p className="mt-1">{lesson.assignment.brief}</p>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          {!locked && courseIdentifier ? (
            <Link
              to={readerTo}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-2xl bg-[#d61f74] px-4 text-sm font-semibold text-white transition hover:bg-[#b91c66]"
            >
              <FaBookOpen className="text-sm opacity-90" />
              Open lesson
            </Link>
          ) : null}
          {(() => {
            const videos = normalizeLessonVideos(lesson);
            if (!videos.length || locked) return null;
            return (
              <>
                {videos.map((url, videoIndex) => (
                  <a
                    key={`${url}-${videoIndex}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex min-h-[44px] max-w-full items-center gap-2 rounded-2xl bg-[#fce7f3] px-4 text-sm font-semibold text-[#b91c66] transition hover:bg-[#fbcfe8]"
                  >
                    <FaVideo className="shrink-0" />
                    <span className="min-w-0 break-words">
                      Video {videos.length > 1 ? videoIndex + 1 : ''}
                    </span>
                  </a>
                ))}
              </>
            );
          })()}
          {attachmentCount > 0 && !locked ? (
            <span className="inline-flex min-h-[44px] max-w-full items-center gap-2 rounded-2xl bg-stone-100 px-4 text-sm font-semibold text-stone-600">
              <FaPaperclip className="shrink-0" />
              <span className="min-w-0 break-words">{attachmentCount} file(s)</span>
            </span>
          ) : null}
          {lesson.assignmentUrl && !locked ? (
            <a
              href={lesson.assignmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] max-w-full items-center gap-2 rounded-2xl bg-stone-100 px-4 text-sm font-semibold text-stone-700 transition hover:bg-stone-200"
            >
              <FaFileDownload className="shrink-0" />
              <span className="min-w-0 break-words">Assignment</span>
            </a>
          ) : null}
          {lesson.tuteUrl && !locked ? (
            <a
              href={lesson.tuteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-[44px] max-w-full items-center gap-2 rounded-2xl bg-stone-100 px-4 text-sm font-semibold text-stone-700 transition hover:bg-stone-200"
            >
              <FaLink className="shrink-0" />
              <span className="min-w-0 break-words">Tutorial</span>
            </a>
          ) : null}
          {!locked ? (
            <button
              type="button"
              onClick={onToggleComplete}
              className={`inline-flex min-h-[44px] items-center gap-2 rounded-2xl px-4 text-sm font-semibold ${
                isCompleted
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
              }`}
            >
              <input
                type="checkbox"
                checked={Boolean(isCompleted)}
                onChange={onToggleComplete}
                className="h-4 w-4 rounded border-stone-300 text-emerald-600"
              />
              {isCompleted ? 'Completed' : 'Mark done'}
            </button>
          ) : null}
        </div>
      </div>
    </StudentCard>
  );
};

const FactCard = ({ label, value }) => (
  <div className="rounded-2xl bg-stone-50 px-4 py-3.5">
    <p className="text-[11px] font-medium text-stone-500">
      {label}
    </p>
    <p className="mt-1.5 text-base font-semibold text-stone-900">{value}</p>
  </div>
);

const BatchInfoItem = ({ label, value }) => (
  <div className="rounded-2xl border border-stone-200 bg-stone-50 px-4 py-4">
    <p className="text-[11px] font-medium text-stone-500">{label}</p>
    <p className="mt-1.5 text-lg font-semibold text-stone-900">{value}</p>
  </div>
);

const BatchMetaBadge = ({ children }) => (
  <span className="inline-flex min-h-[32px] items-center rounded-full bg-stone-100 px-3 text-sm font-medium text-stone-700">
    {children}
  </span>
);

const formatLevel = (value) => {
  if (!value) return 'Not set';
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatMinimumQualifications = (requirements) => {
  if (!requirements) return 'Not set';

  const parts = [];
  if (requirements.olRequired) parts.push('O/L required');
  if (requirements.alRequired) parts.push('A/L required');
  if (requirements.workExperience) {
    parts.push(`${requirements.workExperience} year(s) experience`);
  }

  return parts.length ? parts.join(', ') : 'Open to all students';
};

const formatBatchDate = (dateObj) => {
  if (!dateObj) return 'Not set';
  return new Date(
    dateObj.year,
    dateObj.month - 1,
    dateObj.date,
  ).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const isBatchExpired = (batch) => {
  if (!batch?.startDate?.year || !batch?.startDate?.month || !batch?.startDate?.date) {
    return false;
  }

  const start = new Date(
    batch.startDate.year,
    batch.startDate.month - 1,
    batch.startDate.date,
  );
  start.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return start <= today;
};

export default CourseDetails;
